/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * kSPRestCore — server-safe SharePoint REST client.
 *
 * Provides:
 *   kSPURLs            — typed URL collection for a SharePoint site
 *   SPItemUpdateObj     — field name/value pair for list item updates
 *   RestRequestContext  — per-request state with success/error/retry callbacks
 *   kSPRestCore         — static methods for REST calls, header building, and pure utilities
 *
 * No browser APIs. Safe to import in Node.js, Azure Functions, or the browser.
 *
 * Usage:
 *   1. Create an executor (BrowserRestExecutor or NodeRestExecutor)
 *   2. Call kSPRestCore.Initialize(executor)
 *   3. Use kSPRestCore.RestExecute(...) for all REST calls
 */

import { getCoreUtils, kNotNullOrUndefined, doCallBack } from "./kCoreHandler";
import { kRestExecutor, RestRequestOptions } from "./kRestExecutor";
// SPUserCore imports kSPRestCore but only references it inside method
// bodies (kSPRestCore.RestExecute in SPUserCore.ChangeUser) — never at
// top-level evaluation — so the import cycle resolves cleanly.
import { SPUsersCore } from "./SPUserCore";
import { RestRequestContext } from "./kSPRestRequestCore";
import { kSPRestCoreExecute } from "./kSPRestCoreExecute";
import { kSPRestCoreOData } from "./kSPRestCoreOData";
// Re-exported so every import from "./kSPRestCore" keeps working after the 2026-10-19 split.
export { RestRequestContext } from "./kSPRestRequestCore";

// ── Data classes ─────────────────────────────────────────────────────────────

export class kSPURLs {
    public CurrSiteFullURL: string = "";
    public SiteCollectionRootFullURL: string = "";
    public WebRelativeURL: string = "";
    public HostWebURL: string = "";
    public AppWebURL: string = "";
    public CurrentPageURL: string = "";
    public PageName: string | undefined = "";
    public MySitePersonalURL: string = "";
    public MySiteRootURL: string = "";
    public RootDomainURL: string = "";
}

// SPItemUpdateObj lives in the leaf module kSPItemUpdateObj (2026-10-22, no runtime import cycle); re-exported here.
import { SPItemUpdateObj } from "./kSPItemUpdateObj";
export { SPItemUpdateObj } from "./kSPItemUpdateObj";

// ── Main REST client ─────────────────────────────────────────────────────────

/**
 * Optional PER-INVOCATION executor resolver (see `kFunctionScope`).
 *
 * `Executor` is a module-level singleton, which is right for a browser — one page, one identity —
 * and wrong for an Azure Functions worker, where several invocations run at once and EACH calls
 * `Initialize` with ITS OWN principal. LIVE-CONFIRMED twice on MOSE: a read-only discovery
 * invocation re-pointed the executor underneath an in-flight write and SharePoint answered
 * "Access is denied." on a PATCH that had every right to succeed (2026-07-24, and again 2026-07-28
 * on a discovery write to MoseDiscoveryCache).
 *
 * A host that never registers a resolver is completely unaffected: the getter falls straight
 * through to the process-wide field, which is the behaviour every browser has always had.
 */
let _resolveExecutor: (() => kRestExecutor | null) | null = null;

/** Register (or clear, with null) the per-invocation executor lookup. Node hosts only. */
export const SetExecutorResolver = (resolver: (() => kRestExecutor | null) | null): void => {
    _resolveExecutor = resolver;
};

export class kSPRestCore {
    /** The process-wide executor — the fallback, and the only one a browser ever has. */
    private static _Executor: kRestExecutor;

    /**
     * The executor for the CURRENT execution: the invocation's own when one is scoped, else the
     * process-wide one. A getter rather than a field so every existing reader — the nine call sites
     * in this file and any consumer elsewhere — is scoped without a single call-site edit. Same
     * shape as `RealUsers` below.
     */
    public static get Executor(): kRestExecutor {
        if (_resolveExecutor !== null) {
            try {
                const Scoped: kRestExecutor | null = _resolveExecutor();
                if (kNotNullOrUndefined(Scoped)) { return Scoped as kRestExecutor; }
            } catch (Ignored) {
                // ERROR-SUPPRESSED-JUSTIFIED: this getter is on EVERY REST call. A resolver that
                // throws must degrade to the previous, correct-but-not-concurrency-safe behaviour
                // rather than take down the request — and reporting from here would re-enter the
                // handler through KGlobalErrorHandler's own SharePoint-free path for no gain. A
                // genuinely missing executor is still reported, loudly, by _EnsureExecutorReady.
            }
        }
        return kSPRestCore._Executor;
    }
    public static set Executor(value: kRestExecutor) { kSPRestCore._Executor = value; }

    public static Digest: string = "";
    public static URLs: kSPURLs = new kSPURLs();
    /**
     * Site-users / known-principals cache — single instance shared
     * across browser and server. Browser host populates by querying
     * SiteUserInfoList; browser callers (kSPPeoplePicker, mailto,
     * owner resolution) read it and cast to SPUser where the picture
     * fields are needed. Server callers can read the SPUsersCore
     * shape directly.
     *
     * Methods used (`.Items`, `.GetItemByTitle`, `.GetItemByID`,
     * `.push`) are all on `kObjectArrayCore` so they work server-
     * side. The actual runtime values are `SPUser` instances when the
     * browser populates; that's why browser callers narrow with
     * `as SPUser`.
     */
    // Lazily constructed on FIRST ACCESS, never at module load. This is what breaks the
    // kSPRestCore <-> SPUserCore circular-import trap: an eager `new SPUsersCore()` here executed while
    // SPUserCore was still mid-load whenever kSPRestCore was required first, throwing "SPUsersCore is
    // not a constructor". Deferring construction to first read means neither module references the other
    // at load time (SPUserCore already only touches kSPRestCore inside method bodies — see the top-of-file
    // note), so the cycle is harmless in either import order. The getter always returns the same cached
    // instance; the setter preserves field write-semantics for a host that swaps the cache wholesale.
    private static _RealUsers: SPUsersCore | null = null;
    public static get RealUsers(): SPUsersCore {
        if (kSPRestCore._RealUsers === null) { kSPRestCore._RealUsers = new SPUsersCore(); }
        return kSPRestCore._RealUsers;
    }
    public static set RealUsers(value: SPUsersCore) { kSPRestCore._RealUsers = value; }

    /**
     * Optional self-heal hook invoked by `_EnsureExecutorReady` when `Executor` is missing.
     * Hosts that can construct a fallback executor (e.g. a browser host that has
     * `window._spPageContextInfo`) should register a factory here at module load. The hook
     * runs at most once per missing-executor incident; if it leaves `Executor` set, the REST
     * call proceeds. If the hook is null or fails to set `Executor`, the existing readable
     * "not initialized" error is reported.
     * Reported 2026-04-22: regular-permission users on a restricted-access SharePoint site
     * hit "Executor is not set" on first template click in a fresh browser, suggesting the
     * SPFx host invoked the click before TMSPFX.js's onInit had wired up Executor.
     */
    public static OnExecutorMissing: (() => void) | null = null;

    /**
     * Who wants to hear that SharePoint is THROTTLING - a 429, or the 503 "server too busy" it sends
     * for the same reason - at the moment a request schedules its server-directed retry.
     *
     * WHY A LISTENER AND NOT A RETURN VALUE (2026-09-24, first consumer kSPSiteScanCore). The retry
     * happens INSIDE RestRequestContext, invisible to the caller, which is right for one request and
     * wrong for a caller running many in parallel: kAdaptiveQueueCore halves its window on
     * `HandleThrottleSignal`, but nothing ever told it a throttle had happened, so it could only ever
     * ramp UP. A listener is the smallest change that lets a queue back off without every consumer
     * re-implementing the retry. It is a LIST, not one field (compare kResilientRest.OnThrottled),
     * because two scans on one page must not unhook each other.
     *
     * Notification only: it decides nothing, the retry proceeds exactly as before, and a listener that
     * throws is reported under its own name without disturbing the request.
     */
    private static _ThrottleListeners: Array<(StatusCode: number) => void> = [];

    public static AddThrottleListener = (Listener: (StatusCode: number) => void): void => {
        if (getCoreUtils().KErrorMode) { return; }
        try {
            if (kSPRestCore._ThrottleListeners.indexOf(Listener) < 0) { kSPRestCore._ThrottleListeners.push(Listener); }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kSPRestCore.AddThrottleListener");
        }
    };

    /** DOCUMENTED EXCEPTION: no error-mode guard - removing a listener is cleanup that must still
        happen when a scan settles BECAUSE the gate latched; a guard would leave it hooked for ever. */
    public static RemoveThrottleListener = (Listener: (StatusCode: number) => void): void => {
        try {
            const At: number = kSPRestCore._ThrottleListeners.indexOf(Listener);
            if (At >= 0) { kSPRestCore._ThrottleListeners.splice(At, 1); }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kSPRestCore.RemoveThrottleListener");
        }
    };

    /** Called by RestRequestContext when it schedules a retry for 429 / 503. */
    public static NotifyThrottled = (StatusCode: number): void => {
        if (getCoreUtils().KErrorMode) { return; }
        try {
            for (const Listener of kSPRestCore._ThrottleListeners.slice(0)) { Listener(StatusCode); }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kSPRestCore.NotifyThrottled");
        }
    };

    /**
     * Host-registered "ensure RealUsers cache is populated" hook. Browser
     * hosts register `kSPRest.GetSiteUsers` (which does a SiteUserInfoList
     * REST GET and pushes parsed SPUser rows onto RealUsers); server hosts
     * populate RealUsers up-front from their own auth chain and leave the
     * default — an immediate-callback no-op. Used by code that must wait
     * for the cache before continuing (avatar/owner resolution); cheap
     * defensive call because the typical case is "already warm, invoke
     * callback immediately and proceed". Replaces the ks-side direct
     * `kSPRest.GetSiteUsers` calls that lived in IsoPdfGenerator before
     * the kSPRest2 → kSPRestCore migration.
     */
    public static WarmRealUsers: (cb: any) => void = doCallBack;

    // ── Initialization ───────────────────────────────────────────────────────

    public static Initialize = (ExecutorInstance: kRestExecutor): void => {
        if (getCoreUtils().KErrorMode) { return; }
        try {
            kSPRestCore.Executor = ExecutorInstance;
            kSPRestCore.URLs.CurrSiteFullURL = ExecutorInstance.SiteUrl;
            kSPRestCore.URLs.SiteCollectionRootFullURL = ExecutorInstance.SiteUrl;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kSPRestCore.Initialize");
        }
    };

    /**
     * Verifies that kSPRestCore.Initialize(...) was called before a REST path runs.
     * If the executor is missing, attempts a one-shot self-heal via OnExecutorMissing
     * before reporting via KGlobalErrorHandler. Never throws.
     */
    public static _EnsureExecutorReady = (FunctionName: string): boolean => {
        if (getCoreUtils().KErrorMode) { return false; }
        try {
            if (kNotNullOrUndefined(kSPRestCore.Executor)) { return true; }
            kSPRestCore._TryRecoverExecutor(FunctionName);
            if (kNotNullOrUndefined(kSPRestCore.Executor)) { return true; }
            const MissingExecutorError: Error = new Error(
                "kSPRestCore.Executor is not set. Call kSPRestCore.Initialize(...) before making REST calls."
            );
            MissingExecutorError.name = "kSPRestCore not initialized";
            getCoreUtils().KGlobalErrorHandler(MissingExecutorError, FunctionName);
            return false;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kSPRestCore._EnsureExecutorReady");
            return false;
        }
    };

    /**
     * One-shot recovery path. Calls OnExecutorMissing once and swallows any failure
     * inside the factory so the caller still gets a clean "not initialized" error
     * if recovery did not produce a usable executor.
     */
    private static _TryRecoverExecutor = (FunctionName: string): void => {
        if (getCoreUtils().KErrorMode) { return; }
        try {
            if (!kNotNullOrUndefined(kSPRestCore.OnExecutorMissing)) { return; }
            const Factory: () => void = kSPRestCore.OnExecutorMissing as () => void;
            Factory();
        } catch (e) {
            // The LABEL names the method that failed and the caller's FunctionName rides in
            // the third argument. Concatenating the two into the label produced a different
            // string on every call, so no two reports of the same fault ever grouped together.
            getCoreUtils().KGlobalErrorHandler(e, "kSPRestCore._TryRecoverExecutor", FunctionName);
        }
    };

    // ── Header building ──────────────────────────────────────────────────────

    public static BuildHeaders = (
        Method: string,
        NoXHTTP?: boolean
    ): Record<string, string> => {
        if (getCoreUtils().KErrorMode) { return {}; }
        try {
            const Headers: Record<string, string> = {
                "Accept": "application/json;odata=verbose",
                "Content-Type": "application/json;odata=verbose",
                "IF-MATCH": "*",
            };
            if (NoXHTTP !== true) {
                if (Method !== undefined) {
                    Headers["X-HTTP-Method"] = Method.toUpperCase();
                }
            }
            if (!kSPRestCore._EnsureExecutorReady("kSPRestCore.BuildHeaders")) {
                return Headers;
            }
            const AuthHeaders: Record<string, string> =
                kSPRestCore.Executor.GetAuthHeaders(Method);
            for (const Key of Object.keys(AuthHeaders)) {
                Headers[Key] = AuthHeaders[Key];
            }
            return Headers;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kSPRestCore.BuildHeaders");
            return {};
        }
    };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kSPRestCoreExecute.RestExecute
    public static RestExecute = (CallType: string, RestCall: string, Callback: any, Data?: string | Record<string, any> | Blob | ArrayBuffer | null, Comments?: string, IgnoreError?: boolean, CustomHeader?: string | Record<string, string> | null, Binary?: boolean, ErrorHandler?: any, IsText?: boolean, TolerateStatuses?: number[]): void => {
        return kSPRestCoreExecute.RestExecute(this, CallType, RestCall, Callback, Data, Comments, IgnoreError, CustomHeader, Binary, ErrorHandler, IsText, TolerateStatuses);
    };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kSPRestCoreExecute.RestExecuteWebService
    public static RestExecuteWebService = (Method: string, Url: string, Payload?: string | Record<string, any> | null, Callback?: any, ErrorHandler?: any, IgnoreError?: boolean, Comments?: string, IsText?: boolean, ExtraHeaders?: Record<string, string> | null, TolerateStatuses?: number[], NoRetry: boolean = false): void => {
        return kSPRestCoreExecute.RestExecuteWebService(this, Method, Url, Payload, Callback, ErrorHandler, IgnoreError, Comments, IsText, ExtraHeaders, TolerateStatuses, NoRetry);
    };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kSPRestCoreExecute.RestExecuteExternal
    public static RestExecuteExternal = (RestCall: string, Callback?: any, Comments?: string, IgnoreError?: boolean, TargetWeb?: string, Binary?: boolean, ErrorCB?: any, Method?: string, CustomHeader?: string | Record<string, string> | null, Payload?: string | Record<string, any> | Blob | ArrayBuffer | null, TolerateStatuses?: number[], TimeoutMs?: number, IsText?: boolean): void => {
        return kSPRestCoreExecute.RestExecuteExternal(this, RestCall, Callback, Comments, IgnoreError, TargetWeb, Binary, ErrorCB, Method, CustomHeader, Payload, TolerateStatuses, TimeoutMs, IsText);
    };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kSPRestCoreExecute._SettleUnsent
    private static _SettleUnsent = (ErrorHandler: any, Reason: string): void => {
        return kSPRestCoreExecute._SettleUnsent(this, ErrorHandler, Reason);
    };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kSPRestCoreExecute._BuildRequestOptions
    private static _BuildRequestOptions = (CallType: string, RestCall: string, Data: string | Record<string, any> | Blob | ArrayBuffer | null | undefined, CustomHeader: string | Record<string, string> | null | undefined, Binary: boolean | undefined, IsText: boolean | undefined): RestRequestOptions => {
        return kSPRestCoreExecute._BuildRequestOptions(this, CallType, RestCall, Data, CustomHeader, Binary, IsText);
    };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kSPRestCoreExecute._ApplyHeaders
    private static _ApplyHeaders = (Options: RestRequestOptions, CustomHeader: string | Record<string, string> | null | undefined): void => {
        return kSPRestCoreExecute._ApplyHeaders(this, Options, CustomHeader);
    };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kSPRestCoreExecute._MergeExtraHeaders
    private static _MergeExtraHeaders = (Options: RestRequestOptions, ExtraHeaders: Record<string, string> | null | undefined): void => {
        return kSPRestCoreExecute._MergeExtraHeaders(this, Options, ExtraHeaders);
    };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kSPRestCoreExecute._ApplyPayload
    private static _ApplyPayload = (Options: RestRequestOptions, Data: string | Record<string, any> | Blob | ArrayBuffer | null | undefined): void => {
        return kSPRestCoreExecute._ApplyPayload(this, Options, Data);
    };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kSPRestCoreExecute._SanitizeReplacer
    private static _SanitizeReplacer = (_Key: string, Value: unknown): unknown => { return kSPRestCoreExecute._SanitizeReplacer(this, _Key, Value); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kSPRestCoreExecute.SanitizeTextForWrite
    public static SanitizeTextForWrite = (Value: string): string => { return kSPRestCoreExecute.SanitizeTextForWrite(this, Value); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kSPRestCoreExecute._IsStorableChar
    private static _IsStorableChar = (Code: number): boolean => { return kSPRestCoreExecute._IsStorableChar(this, Code); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kSPRestCoreExecute._BuildRequestContext
    private static _BuildRequestContext = (Options: RestRequestOptions, Callback: any, Comments: string | undefined, IgnoreError: boolean | undefined, ErrorHandler: any, TolerateStatuses?: number[]): RestRequestContext => {
        return kSPRestCoreExecute._BuildRequestContext(this, Options, Callback, Comments, IgnoreError, ErrorHandler, TolerateStatuses);
    };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kSPRestCoreOData.CollectionRows
    public static CollectionRows = (Data: unknown): any[] => { return kSPRestCoreOData.CollectionRows(this, Data); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kSPRestCoreOData.IsCollection
    public static IsCollection = (Data: unknown): boolean => { return kSPRestCoreOData.IsCollection(this, Data); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kSPRestCoreOData.CollectionNextLink
    public static CollectionNextLink = (Data: unknown): string => { return kSPRestCoreOData.CollectionNextLink(this, Data); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kSPRestCoreOData.ETagOf
    public static ETagOf = (Item: any, PreferMinimal: boolean = false): string => { return kSPRestCoreOData.ETagOf(this, Item, PreferMinimal); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kSPRestCoreOData.ETagOrNullOf
    public static ETagOrNullOf = (Item: any, PreferMinimal: boolean = false): string | null => {
        return kSPRestCoreOData.ETagOrNullOf(this, Item, PreferMinimal);
    };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kSPRestCoreOData.EntityOf
    public static EntityOf = (Data: unknown): Record<string, any> => { return kSPRestCoreOData.EntityOf(this, Data); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kSPRestCoreOData.EntityOrNullOf
    public static EntityOrNullOf = (Data: unknown): Record<string, any> | null => { return kSPRestCoreOData.EntityOrNullOf(this, Data); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kSPRestCoreOData.ItemIdOf
    public static ItemIdOf = (Item: Record<string, any>): number => { return kSPRestCoreOData.ItemIdOf(this, Item); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kSPRestCoreOData.EncodePath
    public static EncodePath = (Path: string, ForODataLiteral: boolean = true): string => {
        return kSPRestCoreOData.EncodePath(this, Path, ForODataLiteral);
    };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kSPRestCoreOData.IsAbsoluteHttpUrl
    public static IsAbsoluteHttpUrl = (Url: string): boolean => { return kSPRestCoreOData.IsAbsoluteHttpUrl(this, Url); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kSPRestCoreOData.ListByTitleUrl
    public static ListByTitleUrl = (Title: string): string => { return kSPRestCoreOData.ListByTitleUrl(this, Title); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kSPRestCoreOData.ServerRootURL
    public static ServerRootURL = (): string => { return kSPRestCoreOData.ServerRootURL(this); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kSPRestCoreOData.AddUpdateValueObj
    public static AddUpdateValueObj = (InternalFieldName: string, FieldValue: string): SPItemUpdateObj => {
        return kSPRestCoreOData.AddUpdateValueObj(this, InternalFieldName, FieldValue);
    };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kSPRestCoreOData.MultiChoiceFieldValueSave
    public static MultiChoiceFieldValueSave = (ChosenValues: string[]): any => {
        return kSPRestCoreOData.MultiChoiceFieldValueSave(this, ChosenValues);
    };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kSPRestCoreOData.FieldUrlValue
    public static FieldUrlValue = (URL: string, Description?: string): object | null => {
        return kSPRestCoreOData.FieldUrlValue(this, URL, Description);
    };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kSPRestCoreOData.UserObjectSelectAndExpandString
    public static UserObjectSelectAndExpandString = (UserFieldInternalNames: string[]): string => {
        return kSPRestCoreOData.UserObjectSelectAndExpandString(this, UserFieldInternalNames);
    };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kSPRestCoreOData.EscapeODataFilter
    public static EscapeODataFilter = (Value: string): string => { return kSPRestCoreOData.EscapeODataFilter(this, Value); };
}

// RestRequestContext reaches kSPRestCore through this registration, so kSPRestRequestCore does not import its parent
// at runtime (2026-10-22: import cycle removed). Set once, at module load, before any request can run.
RestRequestContext.Rest = kSPRestCore;
